import React, { useState, useEffect, useRef } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  Stack,
  Paper,
  Divider,
  CircularProgress,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  Tooltip,
  Alert
} from '@mui/material';
import {
  X,
  Send,
  Bot,
  User,
  Plus,
  ChevronDown,
  MessageSquare,
  Trash2,
  FileText,
  FilePlus,
  FileEdit,
  FileX,
  List,
  RefreshCw,
  Paperclip,
  Image as ImageIcon,
  Globe,
  Layers
} from 'lucide-react';

// Tool action icons
const toolIcons = {
  list_sections: <Layers size={14} />,
  list_documents: <List size={14} />,
  read_document: <FileText size={14} />,
  create_document: <FilePlus size={14} />,
  update_document: <FileEdit size={14} />,
  delete_document: <FileX size={14} />,
  fetch_url: <Globe size={14} />
};

// Tool action labels
const toolLabels = {
  list_sections: 'Liste des sections',
  list_documents: 'Liste des documents',
  read_document: 'Lecture de document',
  create_document: 'Création de document',
  update_document: 'Modification de document',
  delete_document: 'Suppression de document',
  fetch_url: 'Récupération de page web'
};

// Message bubble component
const MessageBubble = ({ message, isUser }) => {
  const hasToolCalls = message.toolCalls && message.toolCalls.length > 0;
  const hasAttachments = message.attachments && message.attachments.length > 0;
  
  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: isUser ? 'row-reverse' : 'row',
      alignItems: 'flex-start',
      gap: 1,
      mb: 2
    }}>
      <Avatar 
        sx={{ 
          width: 32, 
          height: 32, 
          bgcolor: isUser ? 'primary.main' : 'rgba(139, 92, 246, 0.2)',
          color: isUser ? '#0a0a0f' : '#8b5cf6'
        }}
      >
        {isUser ? <User size={16} /> : <Bot size={16} />}
      </Avatar>
      
      <Box sx={{ maxWidth: '85%' }}>
        {/* Attached images */}
        {hasAttachments && (
          <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
            {message.attachments.map((att, idx) => (
              <Box
                key={idx}
                component="img"
                src={att.startsWith('data:') ? att : `/uploads/${att}`}
                alt={`Attachment ${idx + 1}`}
                sx={{
                  maxWidth: 200,
                  maxHeight: 150,
                  borderRadius: 2,
                  border: '1px solid rgba(255,255,255,0.1)',
                  objectFit: 'cover',
                  cursor: 'pointer'
                }}
                onClick={() => window.open(att.startsWith('data:') ? att : `/uploads/${att}`, '_blank')}
              />
            ))}
          </Stack>
        )}
        
        <Paper
          sx={{
            p: 2,
            bgcolor: isUser ? 'primary.main' : 'rgba(255, 255, 255, 0.03)',
            color: isUser ? '#0a0a0f' : 'text.primary',
            borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
            border: isUser ? 'none' : '1px solid rgba(255, 255, 255, 0.06)'
          }}
        >
          <Typography 
            variant="body2" 
            sx={{ 
              whiteSpace: 'pre-wrap', 
              wordBreak: 'break-word',
              '& code': {
                bgcolor: isUser ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
                px: 0.5,
                borderRadius: 0.5,
                fontFamily: 'monospace',
                fontSize: '0.85em'
              }
            }}
          >
            {message.content}
          </Typography>
        </Paper>
        
        {/* Tool calls display */}
        {hasToolCalls && (
          <Stack spacing={0.5} sx={{ mt: 1 }}>
            {message.toolCalls.map((tc, idx) => (
              <Chip
                key={idx}
                icon={toolIcons[tc.tool] || <FileText size={14} />}
                label={`${toolLabels[tc.tool] || tc.tool}: ${tc.result?.success ? '✓' : '✗'}`}
                size="small"
                sx={{
                  height: 24,
                  bgcolor: tc.result?.success ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                  color: tc.result?.success ? '#10b981' : '#ef4444',
                  border: '1px solid',
                  borderColor: tc.result?.success ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)',
                  '& .MuiChip-icon': {
                    color: tc.result?.success ? '#10b981' : '#ef4444'
                  }
                }}
              />
            ))}
          </Stack>
        )}
        
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', px: 1 }}>
          {new Date(message.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
        </Typography>
      </Box>
    </Box>
  );
};

function DocEditorDrawer({ open, onClose, submissionId, onDocumentsChanged }) {
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [attachments, setAttachments] = useState([]); // { file, preview, base64 }
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Fetch conversations
  const fetchConversations = async () => {
    try {
      const token = localStorage.getItem('portalToken');
      const res = await fetch(`/api/ai/doc-editor/${submissionId}/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setConversations(data);
      }
    } catch (err) {
      console.error('Fetch conversations error:', err);
    }
  };
  
  // Fetch messages for active conversation
  const fetchMessages = async (convId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('portalToken');
      const res = await fetch(`/api/ai/doc-editor/conversations/${convId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
      }
    } catch (err) {
      console.error('Fetch messages error:', err);
    }
    setLoading(false);
  };
  
  // Create new conversation
  const handleNewConversation = async () => {
    try {
      const token = localStorage.getItem('portalToken');
      const res = await fetch(`/api/ai/doc-editor/${submissionId}/conversations`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const conv = await res.json();
        setConversations(prev => [{ ...conv, title: 'Nouvelle conversation' }, ...prev]);
        setActiveConversation(conv.id);
        setMessages([]);
      }
    } catch (err) {
      console.error('Create conversation error:', err);
    }
  };
  
  // Delete conversation
  const handleDeleteConversation = async (convId, e) => {
    e.stopPropagation();
    if (!window.confirm('Supprimer cette conversation ?')) return;
    
    try {
      const token = localStorage.getItem('portalToken');
      await fetch(`/api/ai/doc-editor/conversations/${convId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setConversations(prev => prev.filter(c => c.id !== convId));
      if (activeConversation === convId) {
        setActiveConversation(null);
        setMessages([]);
      }
    } catch (err) {
      console.error('Delete conversation error:', err);
    }
  };
  
  // Handle file selection
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const newAttachments = await Promise.all(
      files.map(async (file) => {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            resolve({
              file,
              name: file.name,
              type: file.type,
              preview: e.target.result,
              base64: e.target.result // data:image/...;base64,...
            });
          };
          reader.readAsDataURL(file);
        });
      })
    );
    
    setAttachments(prev => [...prev, ...newAttachments]);
    e.target.value = ''; // Reset input
  };
  
  // Remove attachment
  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };
  
  // Send message
  const handleSend = async () => {
    if ((!inputValue.trim() && attachments.length === 0) || !activeConversation || sending) return;
    
    const messageContent = inputValue.trim();
    const currentAttachments = [...attachments];
    setInputValue('');
    setAttachments([]);
    setSending(true);
    setError('');
    
    // Optimistically add user message
    const tempUserMsg = {
      id: 'temp-' + Date.now(),
      role: 'user',
      content: messageContent || '(Image attachée)',
      attachments: currentAttachments.map(a => a.preview),
      createdAt: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMsg]);
    
    try {
      const token = localStorage.getItem('portalToken');
      
      // Prepare attachments for API (base64 images)
      const imageAttachments = currentAttachments
        .filter(a => a.type.startsWith('image/'))
        .map(a => ({
          type: a.type,
          data: a.base64
        }));
      
      const res = await fetch(`/api/ai/doc-editor/conversations/${activeConversation}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          content: messageContent,
          images: imageAttachments
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        
        // Replace temp message with real one and add assistant response
        setMessages(prev => [
          ...prev.filter(m => m.id !== tempUserMsg.id),
          data.userMessage,
          data.assistantMessage
        ]);
        
        // Update conversation title in list
        fetchConversations();
        
        // Notify parent if documents were changed
        if (data.toolResults?.some(tr => 
          ['create_document', 'update_document', 'delete_document'].includes(tr.tool) && 
          tr.result?.success
        )) {
          onDocumentsChanged?.();
        }
      } else {
        const errData = await res.json();
        setError(errData.error || 'Erreur lors de l\'envoi');
        // Remove temp message
        setMessages(prev => prev.filter(m => m.id !== tempUserMsg.id));
      }
    } catch (err) {
      console.error('Send error:', err);
      setError('Erreur de connexion');
      setMessages(prev => prev.filter(m => m.id !== tempUserMsg.id));
    }
    
    setSending(false);
  };
  
  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Fetch conversations when drawer opens
  useEffect(() => {
    if (open && submissionId) {
      fetchConversations();
    }
  }, [open, submissionId]);
  
  // Fetch messages when active conversation changes
  useEffect(() => {
    if (activeConversation) {
      fetchMessages(activeConversation);
    }
  }, [activeConversation]);
  
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 450 },
          bgcolor: 'background.default',
          backgroundImage: 'none'
        }
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ 
          p: 2, 
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ 
              p: 1, 
              bgcolor: 'rgba(139, 92, 246, 0.1)', 
              borderRadius: 2,
              color: '#8b5cf6',
              display: 'flex'
            }}>
              <Bot size={20} />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Assistant Documents
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Créer, modifier, supprimer des documents
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small">
            <X size={20} />
          </IconButton>
        </Box>
        
        {/* Conversations list */}
        <Accordion 
          defaultExpanded={!activeConversation}
          sx={{ 
            bgcolor: 'transparent', 
            '&:before': { display: 'none' },
            boxShadow: 'none'
          }}
        >
          <AccordionSummary 
            expandIcon={<ChevronDown size={16} />}
            sx={{ 
              minHeight: 40,
              '& .MuiAccordionSummary-content': { my: 0 }
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <MessageSquare size={14} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Conversations ({conversations.length})
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 1, pt: 0 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Plus size={16} />}
              onClick={handleNewConversation}
              sx={{ mb: 1, borderStyle: 'dashed' }}
              size="small"
            >
              Nouvelle conversation
            </Button>
            
            <Stack spacing={0.5} sx={{ maxHeight: 150, overflowY: 'auto' }}>
              {conversations.map(conv => (
                <Paper
                  key={conv.id}
                  onClick={() => setActiveConversation(conv.id)}
                  sx={{
                    p: 1.5,
                    cursor: 'pointer',
                    bgcolor: activeConversation === conv.id 
                      ? 'rgba(139, 92, 246, 0.1)' 
                      : 'rgba(255,255,255,0.02)',
                    border: '1px solid',
                    borderColor: activeConversation === conv.id 
                      ? 'rgba(139, 92, 246, 0.3)' 
                      : 'rgba(255,255,255,0.06)',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    '&:hover': { 
                      bgcolor: activeConversation === conv.id 
                        ? 'rgba(139, 92, 246, 0.15)' 
                        : 'rgba(255,255,255,0.04)' 
                    }
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                      {conv.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(conv.updatedAt).toLocaleDateString('fr-FR')}
                    </Typography>
                  </Box>
                  <IconButton 
                    size="small" 
                    onClick={(e) => handleDeleteConversation(conv.id, e)}
                    sx={{ ml: 1, color: '#ef4444', opacity: 0.6, '&:hover': { opacity: 1 } }}
                  >
                    <Trash2 size={14} />
                  </IconButton>
                </Paper>
              ))}
              
              {conversations.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  Aucune conversation
                </Typography>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
        
        <Divider />
        
        {/* Messages area */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          {!activeConversation ? (
            <Box sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center',
              textAlign: 'center',
              color: 'text.secondary'
            }}>
              <Bot size={48} strokeWidth={1} style={{ marginBottom: 16, opacity: 0.5 }} />
              <Typography variant="body1" sx={{ mb: 1 }}>
                Sélectionnez ou créez une conversation
              </Typography>
              <Typography variant="body2">
                L'assistant peut créer, modifier et supprimer des documents pour vous.
              </Typography>
            </Box>
          ) : loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : messages.length === 0 ? (
            <Box sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center',
              textAlign: 'center',
              color: 'text.secondary'
            }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Commencez la conversation
              </Typography>
              <Stack spacing={1}>
                <Chip 
                  label="Crée un document de spécifications" 
                  size="small" 
                  onClick={() => setInputValue('Crée un document de spécifications techniques')}
                  sx={{ cursor: 'pointer' }}
                />
                <Chip 
                  label="Liste tous les documents" 
                  size="small" 
                  onClick={() => setInputValue('Liste tous les documents du projet')}
                  sx={{ cursor: 'pointer' }}
                />
              </Stack>
            </Box>
          ) : (
            <>
              {messages.map((msg) => (
                <MessageBubble 
                  key={msg.id} 
                  message={msg} 
                  isUser={msg.role === 'user'}
                />
              ))}
              <div ref={messagesEndRef} />
            </>
          )}
        </Box>
        
        {/* Error display */}
        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mx: 2, mb: 1 }}>
            {error}
          </Alert>
        )}
        
        {/* Input area */}
        {activeConversation && (
          <Box sx={{ 
            p: 2, 
            borderTop: '1px solid rgba(255,255,255,0.06)',
            bgcolor: 'rgba(0,0,0,0.2)'
          }}>
            {/* Attachments preview */}
            {attachments.length > 0 && (
              <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                {attachments.map((att, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      position: 'relative',
                      width: 60,
                      height: 60,
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: '1px solid rgba(255,255,255,0.1)'
                    }}
                  >
                    {att.type.startsWith('image/') ? (
                      <Box
                        component="img"
                        src={att.preview}
                        alt={att.name}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <Box sx={{ 
                        width: '100%', 
                        height: '100%', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        bgcolor: 'rgba(255,255,255,0.05)'
                      }}>
                        <FileText size={24} />
                      </Box>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => removeAttachment(idx)}
                      sx={{
                        position: 'absolute',
                        top: -4,
                        right: -4,
                        width: 18,
                        height: 18,
                        bgcolor: '#ef4444',
                        color: 'white',
                        '&:hover': { bgcolor: '#dc2626' }
                      }}
                    >
                      <X size={12} />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            )}
            
            <Stack direction="row" spacing={1}>
              {/* Hidden file input */}
              <input
                type="file"
                ref={fileInputRef}
                hidden
                accept="image/*"
                multiple
                onChange={handleFileSelect}
              />
              
              {/* Attach button */}
              <Tooltip title="Joindre une image">
                <IconButton
                  onClick={() => fileInputRef.current?.click()}
                  disabled={sending}
                  sx={{ 
                    color: 'text.secondary',
                    '&:hover': { color: '#8b5cf6' }
                  }}
                >
                  <ImageIcon size={20} />
                </IconButton>
              </Tooltip>
              
              <TextField
                fullWidth
                multiline
                maxRows={4}
                size="small"
                placeholder="Demandez à l'assistant..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                disabled={sending}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'rgba(255,255,255,0.03)'
                  }
                }}
              />
              <Button
                variant="contained"
                onClick={handleSend}
                disabled={(!inputValue.trim() && attachments.length === 0) || sending}
                sx={{ minWidth: 50, px: 2 }}
              >
                {sending ? <CircularProgress size={20} color="inherit" /> : <Send size={18} />}
              </Button>
            </Stack>
          </Box>
        )}
      </Box>
    </Drawer>
  );
}

export default DocEditorDrawer;
